import { createFileRoute, redirect, Link } from '@tanstack/react-router';
import { ChevronLeft } from 'lucide-react';
import { isForecastEnabled, isXeroEnabled } from '@/lib/feature-flags';
import { FinanceGuard } from '@/components/finance/FinanceGuard';
import { FinanceNav } from '@/components/tracking/FinanceNav';
import { FreshnessChip } from '@/components/tracking/FreshnessChip';
import { useFinancialModel } from '@/hooks/use-financial-model';
import { weeklyCashflow, DEFAULT_TAX_DRAWS, latestMetricValue } from '@/lib/financial-calc/forecast';
import { fmtCurrency } from '@/components/tracking/format';
import { TrendChart } from '@/components/tracking/TrendChart';
import type { AccountRole } from '@/types/tracking-view';
import { ForecastToolNav } from '@/components/finance/ForecastToolNav';

/**
 * /finance/forecast/13-week-cashflow — the 13-Week Cashflow tool (F-013 S6.1). A weekly cash-buffer
 * outline derived from the monthly model (true weekly detail needs a bank feed — stated on the page).
 * Real + flag-gated; the first deep-linked forecast sub-tool.
 */
export const Route = createFileRoute('/finance/forecast_/13-week-cashflow')({
  beforeLoad: () => {
    if (!isXeroEnabled() || !isForecastEnabled()) {
      throw redirect({ to: '/finance', replace: true });
    }
  },
  component: () => (
    <FinanceGuard>
      <ThirteenWeekCashflow />
    </FinanceGuard>
  ),
});

function ThirteenWeekCashflow() {
  const { model, input } = useFinancialModel();
  const periods = input?.periods ?? [];
  const cur = periods[periods.length - 1]?.roleTotals ?? {};
  const n = (k: AccountRole) => cur[k] ?? 0;

  const startCash = n('cash-bucket');
  const margin = latestMetricValue(model?.metricsById['gross-margin']) ?? 25;
  const monthlyNet = n('revenue') * (margin / 100) - n('overhead') - DEFAULT_TAX_DRAWS;
  const weeks = weeklyCashflow(startCash, monthlyNet, 13);
  const lowest = weeks.reduce((lo, w) => (w.balance < lo.balance ? w : lo), weeks[0]);

  const history = weeks.map((w) => ({ period: `W${w.week}`, value: w.balance }));

  return (
    <div className="mx-auto w-full max-w-6xl">
      <FinanceNav />
      <Link to="/finance/forecast" className="mt-2 inline-flex items-center gap-1 text-[12px] text-muted-foreground no-underline hover:text-foreground">
        <ChevronLeft className="h-3.5 w-3.5" /> Forecast
      </Link>
      <div className="mt-1 flex flex-wrap items-center justify-between gap-2"><h1 className="ds-h1">13-Week Cashflow</h1><FreshnessChip fetchedAt={model?.fetchedAt} /></div>
      <p className="mb-4 mt-0.5 text-[13px] text-muted-foreground">Your cash buffer over the next quarter, week by week.</p>

      {!model || periods.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-border py-16 text-center text-muted-foreground">
          <p className="font-semibold text-foreground">Needs your Xero data</p>
          <p className="mt-1 text-[12.5px]">Connect Xero and sync to project your cash buffer.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-3.5 lg:grid-cols-3">
          <div className="rounded-2xl border border-border bg-card p-4 lg:col-span-2">
            <p className="mb-2 text-[10.5px] font-bold uppercase tracking-[0.06em] text-muted-foreground">Cash buffer · 13 weeks</p>
            <TrendChart history={history} unit="currency" benchmark={0} benchmarkLabel="empty" height={160} showYAxis />
            <p className="mt-2 text-[11px] text-muted-foreground">Monthly-derived outline — true week-by-week timing needs your bank feed (a later slice).</p>
          </div>
          <div className="rounded-2xl border border-border bg-card p-4">
            <p className="mb-2 text-[10.5px] font-bold uppercase tracking-[0.06em] text-muted-foreground">The squeeze</p>
            <span className="block text-[10px] uppercase tracking-wide text-muted-foreground">Lowest point (week {lowest.week})</span>
            <span className={`ds-kpi-md ${lowest.balance < 0 ? 'text-danger' : 'text-foreground'}`}>{fmtCurrency(lowest.balance)}</span>
            <div className="mt-3 border-t border-border pt-2 text-[12px]">
              <div className="flex justify-between py-0.5"><span className="text-muted-foreground">Starting cash</span><span className="font-mono tabular-nums">{fmtCurrency(startCash)}</span></div>
              <div className="flex justify-between py-0.5"><span className="text-muted-foreground">Net / week</span><span className={`font-mono tabular-nums ${monthlyNet < 0 ? 'text-danger' : 'text-success'}`}>{fmtCurrency(weeks[0].net)}</span></div>
              <div className="flex justify-between py-0.5"><span className="text-muted-foreground">In 13 weeks</span><span className="font-mono tabular-nums">{fmtCurrency(weeks[weeks.length - 1].balance)}</span></div>
            </div>
          </div>
        </div>
      )}
      <ForecastToolNav current="13-week-cashflow" />
    </div>
  );
}
